import { apiService as api } from './apiService';
import { backendImageService } from './backendImageService';

export interface Gift {
    id: string;
    name: string;
    description: string;
    imageUrl: string;
    category?: string;
    price: string | number;
    currency: string;
    stock: number;
    sortOrder?: number;
    isActive: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface GiftOrder {
    id: string;
    customerName: string;
    customerEmail: string;
    totalAmount: string | number;
    currency: string;
    status: 'pending' | 'paid' | 'fulfilled' | 'cancelled';
    transactionRef?: string;
    createdAt?: string;
    updatedAt?: string;
}

// Cache implementation for gifts
let giftsCache: Gift[] = [];
let giftsCacheTimestamp = 0;
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

export const giftApi = {
    // Public
    getAllGifts: async (activeOnly = true) => {
        const useCache = giftsCache.length > 0;
        
        // If we have cache, trigger background refresh if older than 1 hour and return immediately
        if (useCache) {
            const age = Date.now() - giftsCacheTimestamp;
            if (age > CACHE_DURATION) {
                api.request<Gift[]>(`/gifts?activeOnly=${activeOnly}`).then(data => {
                    giftsCache = data;
                    giftsCacheTimestamp = Date.now();
                }).catch(() => {});
            }
            return activeOnly ? giftsCache.filter(g => g.isActive !== false) : [...giftsCache];
        }

        // No cache, fetch from backend
        try {
            const data = await api.request<Gift[]>(`/gifts?activeOnly=${activeOnly}`);
            giftsCache = data;
            giftsCacheTimestamp = Date.now();
            return data;
        } catch (error) {
            return giftsCache.length > 0 ? (activeOnly ? giftsCache.filter(g => g.isActive !== false) : [...giftsCache]) : [];
        }
    },
    getGiftById: async (id: string) => {
        return api.request<Gift>(`/gifts/${id}`);
    },

    // Admin
    createGift: async (data: Partial<Gift>) => {
        const result = await api.request<Gift>('/gifts', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        giftsCache = []; // invalidate cache
        return result;
    },
    updateGift: async (id: string, data: Partial<Gift>) => {
        const result = await api.request<Gift>(`/gifts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        giftsCache = []; // invalidate cache
        return result;
    },
    deleteGift: async (id: string) => {
        const result = await api.request<{ message: string; id: string }>(`/gifts/${id}`, {
            method: 'DELETE',
        });
        giftsCache = []; // invalidate cache
        return result;
    },

    // Orders Admin
    getAllOrders: async () => {
        return api.request<GiftOrder[]>('/gift-orders');
    },
    updateOrderStatus: async (id: string, status: string, transactionRef?: string) => {
        return api.request<GiftOrder>(`/gift-orders/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status, transactionRef }),
        });
    },

    // Image Upload
    uploadImage: async (file: File) => {
        const result = await backendImageService.uploadImage(file);
        return { success: true, imageUrl: result.url };
    },
    // Public Checkout
    createGiftOrder: async (data: any) => {
        return api.request<{ success: boolean; data: GiftOrder; redirectUrl: string }>('/gift-orders', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }
};
